import { useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Play } from 'lucide-react';
import { useAuth } from '../../../services/authentication/authContext';
import { useSubscription } from '../../../hooks/useSubscription';
import { useImpersonationStore } from '../../../stores/appImpersonationStore';
import { canAccessTour, getTour } from '../../onboarding/tours';
import { useOnboardingStore } from '../../onboarding/onboardingStore';
import { tourLaunchIdentity, usePendingTourStore } from '../../onboarding/pendingTourStore';
import { getAccessibleGuides, GUIDE_SECTIONS, SupportGuideArticle } from './supportGuideRegistry';
import { cn } from '../../../lib/utils';
import { xxLargeTextSize, semiBoldText, primaryText } from '../../../styling/stylingHelper';

/** Curated guide copy supports emphasis only; no HTML or arbitrary links. */
function GuideText({ children }: { children: string }) {
  return <>{children.split(/(\*\*[^*]+\*\*)/g).map((part, index) => part.startsWith('**')
    ? <strong key={index} className="font-semibold text-text dark:text-text-dark">{part.slice(2, -2)}</strong>
    : part)}</>;
}

export default function SupportGuidePage() {
  const { guideId } = useParams();
  const { t } = useTranslation('supportGuides');
  const { user, backendUser, accountSettings, loading } = useAuth();
  const { isActive } = useSubscription();
  const impersonation = useImpersonationStore(s => s.target);
  const completed = useOnboardingStore(s => s.completedTours);
  const location = useLocation();
  const navigate = useNavigate();
  const guide = getAccessibleGuides(backendUser, accountSettings, isActive).find(entry => entry.id === guideId);
  const tour = guide?.tourId ? getTour(guide.tourId) : undefined;
  const expectedUserId = impersonation?.userId ?? user?.id;
  const hasTour = Boolean(isActive && tour && canAccessTour(tour, backendUser, accountSettings));
  const article = guide ? t(guide.id, { returnObjects: true }) as unknown as SupportGuideArticle : null;
  useEffect(() => {
    const hash = location.hash.slice(1);
    // Keep older FAQ/bookmarked section links working after the three-section redesign.
    const section = hash === 'source' ? 'what' : ['why', 'actions'].includes(hash) ? 'important' : hash;
    if (guide && GUIDE_SECTIONS.some(id => id === section)) {
      document.getElementById(section)?.scrollIntoView({ block: 'start' });
    }
  }, [guide, location.hash]);
  const launch = () => {
    if (!tour || !hasTour || loading || backendUser?.id !== expectedUserId || !window.matchMedia('(min-width: 1024px)').matches) return;
    const identity = tourLaunchIdentity(user?.id, expectedUserId, Boolean(impersonation));
    const destination = usePendingTourStore.getState().request(tour.id, identity, location.pathname, location.key);
    if (destination) navigate(destination);
  };

  return <main className="mx-auto w-full max-w-4xl p-4 pb-24 sm:p-8 text-text dark:text-text-dark">
    <header className="mb-6 flex items-center gap-2">
      <Link to="/support/guides" aria-label={t('_ui.backGuides')} className={cn('flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center', primaryText)}><ArrowLeft size={24} aria-hidden="true" /></Link>
      <h1 className={cn(xxLargeTextSize, semiBoldText, primaryText)}>{article?.title ?? t('_ui.title')}</h1>
    </header>
    {!guide || !article ? <p role="status">{t('_ui.unavailable')}</p> : <>
      {hasTour && <section aria-labelledby="guide-tour-title" className="mb-5 flex flex-col gap-4 rounded-2xl border border-border dark:border-border-dark bg-cardLightGreen dark:bg-cardLightGreen-dark p-5 sm:flex-row sm:items-center sm:justify-between">
        <div><h2 id="guide-tour-title" className="font-semibold">{t('_ui.tourTitle')}</h2><p className="mt-1 text-sm text-muted dark:text-muted-dark">{t('_ui.tourDescription')}</p></div>
        <button type="button" onClick={launch} disabled={loading || backendUser?.id !== expectedUserId} className="hidden lg:inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-xl border border-border dark:border-border-dark bg-card dark:bg-card-dark px-5 py-3 disabled:opacity-50"><Play size={18} aria-hidden="true" />{t(completed[tour!.id] ? '_ui.replay' : '_ui.start')}</button>
        <p className="lg:hidden text-sm text-muted dark:text-muted-dark">{t('_ui.desktop')}</p>
      </section>}
      <div className="space-y-7 rounded-2xl border border-border dark:border-border-dark bg-card dark:bg-card-dark p-5 sm:p-8">
        <section id="where" className="scroll-mt-24">
          <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
            <h2 className="text-lg font-semibold">{t('_ui.sections.where')}</h2>
            <Link to={guide.path} className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-xl bg-darkGreenColor dark:bg-darkGreenColor-dark px-5 py-3 text-white dark:text-white">{t('_ui.open')}<ArrowRight size={18} aria-hidden="true" /></Link>
          </div>
          <p className="mt-3 leading-relaxed text-muted dark:text-muted-dark"><GuideText>{article.sections.where}</GuideText></p>
        </section>
        <section id="what" className="scroll-mt-24">
          <h2 className="text-lg font-semibold">{t('_ui.sections.what')}</h2>
          {article.sections.what.map((paragraph, index) => <p key={index} className="mt-2 leading-relaxed text-muted dark:text-muted-dark"><GuideText>{paragraph}</GuideText></p>)}
        </section>
        <section id="important" className="scroll-mt-24">
          <h2 className="text-lg font-semibold">{t('_ui.sections.important')}</h2>
          <p className="mt-2 leading-relaxed text-muted dark:text-muted-dark"><GuideText>{article.sections.important.intro}</GuideText></p>
          <ul className="mt-3 list-disc space-y-2 pl-5 leading-relaxed text-muted dark:text-muted-dark">
            {article.sections.important.items.map((item, index) => <li key={index}><GuideText>{item}</GuideText></li>)}
          </ul>
        </section>
      </div>
    </>}
  </main>;
}
